'use client';
import { FC, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TabPanel from '@mui/lab/TabPanel';

import Button from '../button';
import { withDialog } from './with-dialog';
import { ISearchProps, ISearchState } from './interface';
import { SearchInput } from './search-input';
import { Tabs } from './tabs';
import { FiPlus } from '@react-icons/all-files/fi/FiPlus';
import { ContainerOptions } from './styles';
import { GeneralSearchUseCase } from './core/general-search';
import { SearchItemEntity } from './core/entities/search-item';
import { SearchCategory } from './core/search-use-case';
import { SearchList } from './list';
import { withChildren } from './with-children';

const INITIAL_CATEGORY = { label: 'Tudo', disabled: false, slug: 'all' };

export const Search: FC<ISearchProps> = withDialog(
  withChildren(
    ({
      placeholder = 'Pesquise por ações, fundos e muito mais...',
      comparisonMode = false,
      showCloseInputIcon = true,
      disabled = false,
      ...props
    }) => {
      const [categories, setCategories] = useState<SearchCategory[]>([
        INITIAL_CATEGORY,
      ]);

      const [{ isLoading, openList, groupedAssets, tab }, setState] =
        useState<ISearchState>({
          tab: categories?.[0]?.slug,
          isLoading: false,
          openList: false,
          groupedAssets: {},
        });

      const onSearch = async (term: string) => {
        if (term?.length < 2) {
          setCategories([INITIAL_CATEGORY]);
          setState((state) => ({
            ...state,
            isLoading: false,
            openList: false,
            tab: INITIAL_CATEGORY.slug,
            groupedAssets: {},
          }));
          return;
        }

        setState((state) => ({
          ...state,
          isLoading: true,
          openList: true,
          tab: INITIAL_CATEGORY.slug,
        }));

        const useCaseToSearch = props.useCase
          ? props.useCase
          : new GeneralSearchUseCase();
        const { categories, groupedAssets } = await useCaseToSearch.execute(
          term
        );

        const asset = groupedAssets?.['all']?.[0];
        if (
          comparisonMode &&
          groupedAssets?.['all']?.length === 1 &&
          !asset?.isDisabled(props?.comparisonMode)
        ) {
          onSelectAsset(asset);
          return;
        }

        setCategories(categories ? categories : []);
        setState((state) => ({
          ...state,
          isLoading: false,
          groupedAssets,
        }));
      };

      const closeSearchHandler = () => {
        setState((state) => ({
          ...state,
          isLoading: false,
          openList: false,
          showChildren: false,
          groupedAssets: {},
        }));

        if (props.onClose) {
          props.onClose();
        }
        if (props.onClickClose) {
          props.onClickClose();
        }
      };

      const onSelectAsset = (asset: SearchItemEntity) => {
        props.onAdd(asset.toJson());

        closeSearchHandler();
      };

      return (
        <Box position="relative">
          <SearchInput
            handleChange={onSearch}
            handleClose={closeSearchHandler}
            isLoading={isLoading}
            placeholder={placeholder}
            showCloseInputIcon={showCloseInputIcon}
            label={props.label}
            dialogMode={props.dialogMode}
            disabled={disabled}
          />
          <ContainerOptions dialogmode={props.dialogMode?.toString()}>
            <Stack
              p={1}
              mb={1}
              gap={1}
              flexDirection={{ xs: 'column', sm: 'row' }}
              display={
                props?.includeRealIncome || props?.actions ? 'flex' : 'none'
              }
            >
              {props?.actions}

              <Button
                size="small"
                startIcon={<FiPlus />}
                variant="outlined"
                color="inherit"
                sx={{ width: '160px' }}
                onClick={() => {
                  props.onOpenFixedIncomeModal();
                  closeSearchHandler();
                }}
              >
                Renda fixa
              </Button>
            </Stack>

            {openList && (
              <Tabs
                selectedTab={tab}
                onTabChange={(value) =>
                  setState((state) => ({ ...state, tab: value }))
                }
                categories={categories}
              >
                {categories.map((category) => (
                  <TabPanel
                    key={category.slug}
                    value={category.slug}
                    sx={{ p: 0 }}
                  >
                    <SearchList
                      comparisonMode={comparisonMode}
                      hiddenExtras={props.hiddenExtras}
                      isLoading={isLoading}
                      onSelectAsset={onSelectAsset}
                      assets={groupedAssets?.[category.slug]}
                    />
                  </TabPanel>
                ))}
              </Tabs>
            )}
          </ContainerOptions>
        </Box>
      );
    }
  )
);
